import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import WithAccountModal from '@components/hocs/WithAccountModal';
import ConnectionPanel from '@components/Dashboard/DashboardConnection';

import iframeMixin from '@/mixins/iframe.js';
import automationMixin from '@/mixins/automation.js';

import { Iframe, PageLoaderWrapper } from '@/styles/common.styled.component';

import {
    ACCOUNT_ROLES_NEW,
    IFRAME_EVENT_MESSAGE,
    REACT_BASE_URL,
} from '@/utils/consts';
import { BUSINESS_PAGES } from '@consts';

import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

import {
    map,
    distinctUntilChanged,
    filter,
    debounceTime,
    switchMap,
    shareReplay,
} from 'rxjs/operators';

const CreativeModal = () =>
    import('@/components/Dashboard/common/CreativeModal');

import WidgetSettingPanel from '@/components/Dashboard/common/WidgetSettingPanel';
import { WidgetErrorPanel } from '@/components/Dashboard/widget/WidgetErrorPanel';

import { isValueExist, numeral } from '@/utils/utils';
import { debounce } from 'lodash';
import { isReportedReactRoute } from '@/utils/dashboardRouteSync';
import RingLoader from '@/components/Common/RingLoader';

const ReactDashboard = {
    name: 'ReactDashboard',
    mixins: [automationMixin, iframeMixin],
    beforeRouteUpdate(to, from, next) {
        const isViewChange =
            to.params?.id && from.params?.id && to.params.id !== from.params.id;

        // A query-only change never leaves a view. The replace the host issues
        // for React's own SYNC_ROUTE is React's view switch, already guarded on
        // its side — it is recognised by content, not by `update=true`, which
        // every dashboard URL in history carries (browser BACK to another view
        // must still be guarded, TASK-13798).
        if (!isViewChange || isReportedReactRoute(to, this.reactRoute)) {
            next();
            return;
        }
        this.guardEditModeLeave(to, next);
    },
    beforeRouteLeave(to, from, next) {
        this.guardEditModeLeave(to, (allowed) => {
            // A SYNC_ROUTE still debounced from the loading dashboard must
            // not replace the route once we have agreed to leave it.
            if (allowed !== false) this.applyReactRoute.cancel();
            next(allowed);
        });
    },
    watch: {
        subscriptionWarningVisible: {
            immediate: true,
            handler(v) {
                if (v) {
                    this.connectionPanel = false;
                }
            },
        },
        openWidgetSettingPanel: {
            handler(v) {
                v ? this.hideChatbot() : this.loadChatbot();
            },
        },
    },
    methods: {
        ...mapMutations('newDashboard', [
            'setGoalRangeSettingData',
            'setTableAdAccounts',
            'setTeamspaceAllowedChannelIds',
            'setTeamspaceAllowedAssetIds',
            'setSelectedTab',
            'setCustomMetrics',
            'updateEditingWidget',
            'setViews',
            'setDimensions',
            'setWidgetError',
            'setWidgetStatusPanelWidgetID',
            'setDataSources',
            'setIsTakeSnapshotLoading',
            'setMetricAliases',
            'setShouldCloseAllSecondPanel',
            'setCurrentWidgetTitlesById',
            'setIsWidgetFilterOpen',
            'setMetricPanelData',
            'setBreakdownPanelData',
            'setCustomBreakdownPanelData',
        ]),
        ...mapActions('newDashboard', [
            'getTableAdAccounts',
            'getDimensionList',
            'getCustomMetrics',
            'getFieldPreferences',
        ]),
        ...mapMutations('view', [
            'addNotiMsg',
            'setNotiMsgs',
            'replaceNotiMsg',
            'removeNotiMsg',
            'setLogoutModalActive',
            'setIsNewCampaignModalActive',
            'setShowCsmToast',
            'setSnackbarSafeBottom',
        ]),
        ...mapActions('iframe', [
            'postIframeMessage',
            'addIframeRef',
            'removeIframeRef',
        ]),
        ...mapActions('asset', ['fetchAssets']),
        ...mapMutations('iframe', ['setRectFromReact']),
        ...mapActions('user', ['getBoAccessAccount', 'fetchMembers']),
        ...mapMutations('user', ['setDashboardClient']),

        clearReactDateFilter() {
            this.setRectFromReact(undefined);

            this.postIframeMessage({
                type: IFRAME_EVENT_MESSAGE.TOGGLE_WIDGET_DATE_FILTER,
                origin: `VUE`,
                data: {
                    open: false,
                },
            });
        },
        closeReactPanels() {
            // widget setting panel 닫힐 때 열려있는 second panel도 함께 닫기
            this.setMetricPanelData(null);
            this.postIframeMessage({
                origin: 'VUE',
                type: IFRAME_EVENT_MESSAGE.TOGGLE_METRIC_PANEL,
                data: null,
            });
            this.setBreakdownPanelData(null);
            this.postIframeMessage({
                origin: 'VUE',
                type: IFRAME_EVENT_MESSAGE.TOGGLE_BREAKDOWN_PANEL,
                data: null,
            });
        },
        checkRoleAndRedirect$() {
            // Wait for account to be loaded before checking role
            const account$ = this.generateWatchNew$('account').pipe(
                filter(isValueExist)
            );

            this.$subscribeTo(account$, () => {
                if (
                    R.equals(this.account?.role, [
                        ACCOUNT_ROLES_NEW.CONNECTION_MANAGEMENT,
                    ])
                ) {
                    this.$router.replace({ path: '/' });
                }
            });
        },
        observeQuery$() {
            const query$ = this.generateWatchNew$('$route.query', {
                deep: true,
            }).pipe(distinctUntilChanged(R.equals), shareReplay(1));

            this.$subscribeTo(query$, (query) => {
                let userAccountIdFromQuery = query.userAccountId;

                if (userAccountIdFromQuery == 0) {
                    userAccountIdFromQuery = undefined;
                }
                if (
                    // fill userAccountId if not exist for debugging purpose
                    (!userAccountIdFromQuery && query.accountId) ||
                    // if user is not admin, can not use other userAccountId
                    (!this.isAdmin &&
                        userAccountIdFromQuery !== query.accountId)
                ) {
                    this.$router.replace({
                        query: R.assoc('userAccountId', query.accountId, query),
                    });
                }
            });
        },
        observeTeamspaceConfig$() {
            const teamspaceConfig$ = this.generateWatchNew$('currentView', {
                deep: true,
            }).pipe(
                map(
                    R.pathOr({}, [
                        'teamspace',
                        'config',
                        'numberFormat',
                        'delimiters',
                    ])
                ),
                filter(isValueExist),
                distinctUntilChanged(R.equals)
            );

            this.$subscribeTo(teamspaceConfig$, (delimiters) => {
                const currentLocale = numeral.locales[this.userLang];

                if (currentLocale) {
                    numeral.locales[this.userLang].delimiters = delimiters;
                    numeral.locale(this.userLang);
                }
            });
        },

        renderCreativeModal() {
            if (this.isMobileSize || !this.openCreativeModal) return;

            const modalProps = {
                attrs: {
                    isModalActive: true,
                    values: this.creativeModalValue,
                },
                on: {
                    'update:active': (v) => {
                        this.openCreativeModal = v;
                        if (!v) {
                            this.creativeModalValue = {};
                        }
                    },
                },
            };

            return <CreativeModal {...modalProps} />;
        },
        renderWidgetSettingPanel() {
            if (this.isMobileSize || !this.openWidgetSettingPanel) return;

            return (
                <WidgetSettingPanel
                    isTemplateView={this.isTemplateView}
                    pendingExposureListKey={this.pendingExposureListKey}
                    onClose={() => {
                        // prevent closing panel when dateFilter is opened
                        if (isValueExist(this.rectFromReact)) {
                            this.clearReactDateFilter();
                            return;
                        }

                        this.setDimensions([]);
                        this.setCustomMetrics([]);
                        this.setTableAdAccounts([]);
                        this.setTeamspaceAllowedChannelIds(undefined);
                        this.setTeamspaceAllowedAssetIds(undefined);
                        this.updateEditingWidget({});
                        this.setViews([]);
                        this.setSelectedTab(undefined);

                        this.openWidgetSettingPanel = false;
                        this.isTemplateView = false;
                        this.pendingExposureListKey = null;

                        this.closeReactPanels();
                    }}
                />
            );
        },
        renderErrorWidgetPanel() {
            if (!this.renderWidgetStatusPanel) return;

            return (
                <WidgetErrorPanel
                    active={true}
                    onCancel={() => this.setWidgetStatusPanelWidgetID(null)}
                    widget={
                        this.currentWidgetsById?.[
                            this.widgetStatusPanelWidgetID
                        ]
                    }
                    widgetError={
                        this.widgetError?.[this.widgetStatusPanelWidgetID]
                    }
                />
            );
        },
        fetchData$() {
            const { generateWatchNew$: gen$, dashboardClientCreated$ } = this;

            const combined$ = dashboardClientCreated$.pipe(
                filter(R.identity),
                switchMap(() => gen$('_accountId')),
                map(Number),
                filter(R.identity),
                debounceTime(50),
                distinctUntilChanged()
            );

            this.$subscribeTo(combined$, (userAccountId) => {
                this.fetchMembers();

                if (this.isAdmin && userAccountId) {
                    this.getBoAccessAccount(userAccountId);
                }
            });
        },
        renderConnectionPanel() {
            const {
                connectionPanel,
                getTableAdAccounts,
                accountToken,
                subscriptionWarningVisible,
            } = this;
            if (
                this.isMobileSize ||
                !connectionPanel ||
                subscriptionWarningVisible
            )
                return;

            const panelProps = {
                attrs: {
                    active: connectionPanel,
                    token: accountToken,
                    showAllDataStream: true,
                    closeOuside: false,
                },
                on: {
                    close: () => (this.connectionPanel = false),
                    refresh: getTableAdAccounts,
                },
            };
            return <ConnectionPanel {...panelProps} />;
        },
        openConnectionPanel() {
            this.connectionPanel = true;
        },
        /**
         * Edit-mode leave guard, decided from the state React mirrors through
         * SYNC_EDIT_MODE_GUARD_DIRTY rather than by asking and waiting. React
         * still gets CHECK_EDIT_MODE_DIRTY: clean, it ends a clean edit
         * session; dirty, it opens its leave modal and re-runs the route with
         * EDIT_MODE_NAV_PROCEED after Discard or Publish. Holding `next()`
         * for React's answer (up to 3s while the dashboard was still loading
         * and could not answer) let a SYNC_ROUTE replace land mid-wait and
         * cancel the navigation — the nudge's [Set up 2FA] never reached
         * Settings (TASK-14322).
         */
        guardEditModeLeave(to, next) {
            if (this.$store.state.view.isForcingLogout) {
                next();
                return;
            }
            this.postIframeMessage({
                type: IFRAME_EVENT_MESSAGE.CHECK_EDIT_MODE_DIRTY,
                origin: 'VUE',
                data: { pendingRoute: to.fullPath },
            });
            if (this.isEditModeGuardDirty) {
                next(false);
            } else {
                next();
            }
        },
        /** Makes the Vue route follow a SYNC_ROUTE from React (debounced). */
        applyReactRouteNow(data) {
            // Stale after leaving: the debounce can fire once the route has
            // moved on, and a replace here would pull the user back.
            if (this.$route.name !== 'Dashboard') return;

            const { query = {} } = data || {};
            const viewId = R.pathOr(null, ['viewId'], query);
            if (!viewId) return;

            const refinedQuery = R.omit(
                [
                    'viewId',
                    'locale',
                    'country',
                    'baseUrl',
                    'userAccountId',
                    'accountId',
                ],
                query
            );
            this.reactRoute = { viewId: String(viewId), query: refinedQuery };
            // The accountId beforeEach redirects this replace (refinedQuery
            // omits accountId); the redirected route still matches
            // `reactRoute`, so neither the guard nor the $route watcher
            // treats it as a new navigation.
            this.$router.replace({
                name: 'Dashboard',
                params: { id: viewId },
                query: { ...refinedQuery, update: true },
            });
        },
        sync$() {
            const { generateWatchNew$: gen$ } = this;
            const inited$ = gen$('cAccountId').pipe(
                filter(R.identity),
                debounceTime(200),
                shareReplay(1)
            );

            this.$subscribeTo(inited$, () => {
                this.addIframeRef(this.$refs.iframeReactDashboard_);
                // [TODO] panel 이 열릴 때 ref를 추가하도록
                this.addIframeRef(this.$refs.iframeGoalRangeSetting);
                this.addIframeRef(this.$refs.iframeFilterPanel);
                this.addIframeRef(this.$refs.iframePanel);
            });

            const user$ = inited$.pipe(
                switchMap(() => gen$('globalUser').pipe(filter(isValueExist)))
            );

            this.$subscribeTo(user$, (user) => {
                this.postIframeMessage({
                    type: `SYNC_USER`,
                    origin: `VUE`,
                    data: {
                        user,
                    },
                });
            });

            const account$ = inited$.pipe(
                switchMap(() => gen$('account').pipe(filter(isValueExist)))
            );

            this.$subscribeTo(account$, (account) => {
                this.postIframeMessage({
                    type: `SYNC_ACCOUNT`,
                    origin: `VUE`,
                    data: {
                        account,
                    },
                });

                if (account.meta.hideDashboard) {
                    this.pushToRoute({
                        name: account.meta.allowDatastream
                            ? 'DataTransfer'
                            : this.isPlanA
                            ? 'MyCampaigns'
                            : 'Connection',
                    });
                }
            });

            const route$ = inited$.pipe(switchMap(() => gen$('$route')));

            this.$subscribeTo(route$, (route) => {
                const { params = {}, query = {} } = route;
                // A route that already matches what React last reported is
                // the host's own replace for its SYNC_ROUTE — echoing it back
                // would loop. Anything else, a browser BACK/FORWARD to another
                // dashboard URL included, is synced (TASK-13798).
                if (
                    params?.id &&
                    !isReportedReactRoute(route, this.reactRoute)
                ) {
                    this.postIframeMessage({
                        type: `SYNC_ROUTE`,
                        origin: `VUE`,
                        data: {
                            query: {
                                ...R.pick(
                                    ['accountId', 'userAccountId'],
                                    this.$route.query
                                ),
                                ...query,
                                viewId: params?.id,
                            },
                        },
                    });
                }
            });
        },
        unLoadEvent: function (event) {
            if (this.$store.state.view.isForcingLogout) return;
            if (!this.isTakeSnapshotLoading && !this.isEditModeGuardDirty)
                return;

            event.preventDefault();
            event.returnValue = '';
        },
    },
    data() {
        return {
            queryParams: undefined,
            currentId: undefined,
            openCreativeModal: false,
            creativeModalValue: {},
            openWidgetSettingPanel: false,
            isTemplateView: false,
            iframeLayer: 'none',
            pendingExposureListKey: null,

            connectionPanel: false,
            isEditModeGuardDirty: false,
            filterHeight: undefined,
            filterVisible: true,
            iframeListeners_: [
                {
                    eventType:
                        IFRAME_EVENT_MESSAGE.TOGGLE_GOAL_WIDGET_RANGE_SETTING,
                    listener: (event) => {
                        const { childWidgetData = {} } = event.data;

                        this.setGoalRangeSettingData(childWidgetData);
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.REFRESH_WIDGET_DATA,
                    listener: () => {
                        this['widgetRefresh$']?.next();
                        this.postIframeMessage({
                            type: IFRAME_EVENT_MESSAGE.REFRESH_WIDGET_DATA,
                            origin: 'VUE',
                            data: {},
                        });
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE?.POST_RECT_FOR_OVERLAY,
                    listener: (event) => {
                        const { rect } = event.data || {};
                        this.setRectFromReact(rect);
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.TOGGLE_HEADER,
                    listener: (event) => {
                        const { showHeader, filterHeight } = event.data;
                        this.filterVisible = showHeader;
                        this.filterHeight = filterHeight;
                    },
                },
                {
                    eventType:
                        IFRAME_EVENT_MESSAGE.CLICK_COPY_CONNECTION_SHARE_LINK,
                    listener: (event) => {
                        const params = {
                            params: { id: event.data.viewId },
                            query: R.path(['$route', 'query'], this),
                        };

                        this.$router.replace(params);
                    },
                },
                // Notification messages
                {
                    eventType: IFRAME_EVENT_MESSAGE.ADD_NOTI_MSGS,
                    listener: (event) => {
                        if (this.isMobileSize && !event.data.mobileVisible)
                            return;
                        this.addNotiMsg(event.data);
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.SET_NOTI_MSGS,
                    listener: (event) => {
                        const visibleData = event.data?.filter(
                            ({ mobileVisible }) => mobileVisible
                        );
                        const data = this.isMobileSize
                            ? visibleData
                            : event.data;
                        this.setNotiMsgs(data || []);
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.REPLACE_NOTI_MSGS,
                    listener: (event) => {
                        if (this.isMobileSize && !event.data.mobileVisible)
                            return;

                        this.replaceNotiMsg(event.data);
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.REMOVE_NOTI_MSG,
                    listener: (event) => {
                        this.removeNotiMsg(event.data);
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.SET_TOAST_MSG,
                    listener: (event) => {
                        if (this.isMobileSize && !event.data.mobileVisible)
                            return;

                        this.setToast(event.data);
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.UPDATE_SNACKBAR_SAFE_BOTTOM,
                    listener: (event) => {
                        if (event?.data?.value !== undefined) {
                            this.setSnackbarSafeBottom(event.data.value);
                        }
                    },
                },
                // router
                {
                    eventType:
                        IFRAME_EVENT_MESSAGE.OPEN_NEW_WINDOW_WITH_FILTERS,
                    listener: (event) => {
                        const groupFilters = R.pathOr(
                            [],
                            ['data', 'groupFilters'],
                            event
                        );
                        const { href } = this.$router.resolve({
                            query: R.assoc(
                                'groupFilters',
                                JSON.stringify(groupFilters),
                                this.$route.query
                            ),
                        });
                        href && window.open(href, '_blank');
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.SYNC_ROUTE,
                    listener: (event) => this.applyReactRoute(event?.data),
                },
                // open creative modal
                {
                    eventType: IFRAME_EVENT_MESSAGE.OPEN_CREATIVE_MODAL,
                    listener: (event) => {
                        const values = R.pathOr({}, ['data', 'values'], event);
                        this.openCreativeModal = true;
                        this.creativeModalValue = values;
                    },
                },
                // GNB
                {
                    eventType: IFRAME_EVENT_MESSAGE.PUSH_TO_ROUTE,
                    listener: (event) => {
                        if (event?.data?.route) {
                            const { route } = event.data;
                            if (route === 'logo') {
                                if (
                                    this.$route.name
                                        .toLowerCase()
                                        .includes('sign')
                                ) {
                                    window.location.href =
                                        this.language === 'ko'
                                            ? BUSINESS_PAGES.kr
                                            : BUSINESS_PAGES.en;
                                } else {
                                    this.pushToRoute({
                                        name: this.isPlanT
                                            ? 'Dashboard'
                                            : 'MyCampaigns',
                                    });
                                }
                            } else if (route === 'new_campaign') {
                                //TODO: add Select new_campagin (when is korean)
                                this.setIsNewCampaignModalActive(true);
                                return;
                            } else {
                                this.pushToRoute(route);
                            }
                        }
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.CHANGE_ACCOUNT,
                    listener: (event) => {
                        if (event?.data?.id) {
                            this.changeAccount(
                                event.data.id,
                                this.routeMeta.canChangeAccount
                            );
                        }
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.SHOW_LOGOUT_MODAL,
                    listener: () => {
                        this.setLogoutModalActive(true);
                    },
                },

                // Sync Account
                {
                    eventType: IFRAME_EVENT_MESSAGE.PAGE_LOAD_EVENT,
                    listener: () => {
                        if (this.account) {
                            this.postIframeMessage({
                                type: `SYNC_ACCOUNT`,
                                origin: `VUE`,
                                data: {
                                    account: this.account,
                                },
                            });
                        }

                        if (this.user) {
                            this.postIframeMessage({
                                type: `SYNC_USER`,
                                origin: `VUE`,
                                data: {
                                    user: this.user,
                                },
                            });
                        }
                    },
                },
                // open widget setting panel
                {
                    eventType: IFRAME_EVENT_MESSAGE.OPEN_WIDGET_SETTING_PANEL,
                    listener: (event) => {
                        const {
                            dimensions,
                            customMetrics,
                            aliasMetrics,
                            tableAdAccounts,
                            editingWidget,
                            views,
                            selectedTab,
                            currentWidgetTitlesById,
                            isTemplateView,
                            teamspaceAllowedChannelIds,
                            teamspaceAllowedAssetIds,
                        } = event.data || {};

                        this.setDimensions(dimensions);
                        this.setCustomMetrics(customMetrics);
                        this.setMetricAliases(aliasMetrics);
                        this.setTableAdAccounts(tableAdAccounts);
                        this.setTeamspaceAllowedChannelIds(
                            teamspaceAllowedChannelIds
                        );
                        this.setTeamspaceAllowedAssetIds(
                            teamspaceAllowedAssetIds
                        );
                        this.updateEditingWidget(editingWidget);
                        this.setViews(views);
                        this.setSelectedTab(selectedTab);
                        this.setCurrentWidgetTitlesById(
                            currentWidgetTitlesById
                        );

                        this.openWidgetSettingPanel = true;
                        this.isTemplateView = isTemplateView;
                    },
                },
                // open exposure list panel (from React FieldMenuDropdown)
                {
                    eventType: IFRAME_EVENT_MESSAGE.OPEN_EXPOSURE_LIST_PANEL,
                    listener: (event) => {
                        const {
                            dimensions,
                            customMetrics,
                            aliasMetrics,
                            tableAdAccounts,
                            editingWidget,
                            views,
                            selectedTab,
                            isTemplateView,
                            exposureListKey,
                            teamspaceAllowedChannelIds,
                            teamspaceAllowedAssetIds,
                        } = event.data || {};

                        this.setDimensions(dimensions);
                        this.setCustomMetrics(customMetrics);
                        this.setMetricAliases(aliasMetrics);
                        this.setTableAdAccounts(tableAdAccounts);
                        this.setTeamspaceAllowedChannelIds(
                            teamspaceAllowedChannelIds
                        );
                        this.setTeamspaceAllowedAssetIds(
                            teamspaceAllowedAssetIds
                        );
                        this.updateEditingWidget(editingWidget);
                        this.setViews(views);
                        this.setSelectedTab(selectedTab);

                        this.pendingExposureListKey = exposureListKey;
                        this.openWidgetSettingPanel = true;
                        this.isTemplateView = isTemplateView;
                    },
                },
                // open widget error panel
                {
                    eventType: IFRAME_EVENT_MESSAGE.OPEN_WIDGET_ERROR_PANEL,
                    listener: (event) => {
                        const {
                            statusPanelWidgetId,
                            selectedTab,
                            views,
                            widgetError,
                            tableAdAccounts,
                            dataSources,
                        } = event.data || {};

                        this.setSelectedTab(selectedTab);
                        this.setViews(views);
                        this.setWidgetError(widgetError);
                        this.setTableAdAccounts(tableAdAccounts);
                        // this sender carries no scope, so drop the previous one
                        this.setTeamspaceAllowedChannelIds(undefined);
                        this.setTeamspaceAllowedAssetIds(undefined);
                        this.setDataSources({ dataSources });

                        this.setWidgetStatusPanelWidgetID(statusPanelWidgetId);
                    },
                },
                // move to other page
                {
                    eventType: IFRAME_EVENT_MESSAGE.MOVE_TO_OTHER_PAGE,
                    listener: (event) => {
                        const { params } = event.data || {};

                        const { name, additionalQuery = {} } = params;

                        const { query = {} } = this.$route;

                        this.$router.push({
                            name,
                            query: { ...query, ...additionalQuery },
                        });
                    },
                },
                // open connection panel
                {
                    eventType: IFRAME_EVENT_MESSAGE.OPEN_CONNECTION_PANEL,
                    listener: () => {
                        this.openConnectionPanel();
                    },
                },
                // lite version
                {
                    eventType: IFRAME_EVENT_MESSAGE.RENDER_LITE_VERSION_DIALOG,
                    listener: () => {
                        this.renderWarningToLimiedUser();
                    },
                },
                // close panels
                {
                    eventType: IFRAME_EVENT_MESSAGE.CLOSE_VUE_PANNELS,
                    listener: () => {
                        this.connectionPanel = false;
                        this.isTemplateView = false;
                        this.openWidgetSettingPanel = false;
                        this.openCreativeModal = false;
                        this.setTeamspaceAllowedChannelIds(undefined);
                        this.setTeamspaceAllowedAssetIds(undefined);
                        this.setWidgetStatusPanelWidgetID(null);

                        this.closeReactPanels();
                    },
                },
                // reload with noCache=1
                {
                    eventType: IFRAME_EVENT_MESSAGE.RELOAD_WITH_NO_CACHE,
                    listener: () => {
                        let href = window.location.href;
                        if (!href.includes('noCache')) {
                            href += '&noCache=1';
                        }

                        window.location.href = href;
                        window.location.reload();
                    },
                },
                // {
                //     eventType: IFRAME_EVENT_MESSAGE.ACTIVATE_WINDOW_SCROLL,
                //     listener: () => {
                //         document
                //             .querySelector('html')
                //             .classList.remove('overflow-hidden');
                //     },
                // },
                {
                    eventType: IFRAME_EVENT_MESSAGE.IS_TAKE_SNAPSHOT_LOADING,
                    listener: (e) => {
                        const isTakeSnapshotLoading = R.pathOr(
                            false,
                            ['data', 'isTakeSnapshotLoading'],
                            e
                        );
                        this.setIsTakeSnapshotLoading(isTakeSnapshotLoading);
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.Loaded_New_Template,
                    listener: () => {
                        const { showCsmToast, setShowCsmToast, setNotiMsgs } =
                            this;

                        if (!showCsmToast) return;

                        setNotiMsgs([
                            {
                                type: 'SUCCESS',
                                title: this.$t('CSM_SERVICE.submit_success'),
                            },
                        ]);

                        setShowCsmToast(false);
                    },
                },
                // chatbot
                {
                    eventType: IFRAME_EVENT_MESSAGE.HIDE_CHATBOT,
                    listener: () => {
                        this.hideChatbot();
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.SHOW_CHATBOT,
                    listener: () => {
                        this.loadChatbot();
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.OPEN_HELP_DESK,
                    listener: () => {
                        this.loadChatbot(true);
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.CLOSE_ALL_SECOND_PANEL,
                    listener: (event) => {
                        const { isClose } = event.data;
                        this.setShouldCloseAllSecondPanel(isClose);
                    },
                },
                /**
                 * [React] 커스텀 메트릭 패널에서 커스텀 메트릭 생성 및 수정 후 (pages/panels)
                 */
                {
                    eventType: IFRAME_EVENT_MESSAGE.REFRESH_CUSTOM_METRICS,
                    listener: (event) => {
                        const { viewId } = event.data || {};

                        /**
                         * [Vue] 위젯 패널의 커스텀 메트릭 업데이트
                         */
                        this.getCustomMetrics(viewId);

                        /**
                         * [React] 대시보드의 커스텀 메트릭 업데이트 (page/dashboard)
                         */
                        this.postIframeMessage({
                            type: IFRAME_EVENT_MESSAGE.REFRESH_CUSTOM_METRICS,
                            origin: `VUE`,
                        });
                    },
                },
                /**
                 * [React] 필드 패널에서 메트릭 별칭 업데이트 후 (pages/panels)
                 */
                {
                    eventType: IFRAME_EVENT_MESSAGE.REFRESH_PREFERENCES,
                    listener: () => {
                        /**
                         * [Vue] 위젯 패널의 메트릭 별칭 업데이트
                         */
                        this.getFieldPreferences();

                        /**
                         * [React] 대시보드의 메트릭 별칭 업데이트 (page/dashboard)
                         */
                        this.postIframeMessage({
                            type: IFRAME_EVENT_MESSAGE.REFRESH_PREFERENCES,
                            origin: `VUE`,
                        });
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.REFRESH_PREFERENCES,
                    listener: () => {
                        /**
                         * [Vue] 위젯 패널의 메트릭 별칭 업데이트
                         */
                        this.getFieldPreferences();

                        /**
                         * [React] 대시보드의 메트릭 별칭 업데이트
                         */
                        this.postIframeMessage({
                            type: IFRAME_EVENT_MESSAGE.REFRESH_PREFERENCES,
                            origin: `VUE`,
                        });
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.OPEN_GLOBAL_TOAST,
                    listener: (event) => {
                        const { message, time, isError, style, position } =
                            event.data || {};

                        this.redesignedToast({
                            message,
                            time,
                            isError,
                            style,
                            position,
                        });
                    },
                },
                // filter panel
                {
                    eventType: IFRAME_EVENT_MESSAGE.TOGGLE_WIDGET_FILTER,
                    listener: () => {
                        this.setIsWidgetFilterOpen(false);
                    },
                },
                {
                    // React customBreakdown 패널이 breakdown 생성/수정/삭제 성공 후
                    // REFRESH_DIMENSIONS 를 보낸다. Vuex dimension list 를 재조회해
                    // Edit Breakdowns 패널 목록을 갱신하고, dashboard iframe 으로 relay
                    // 해 테이블 dimension 도 갱신한다. (원본 Vue BreakdownPanel 저장
                    // 성공 동작 getDimensionList + REFRESH_DIMENSIONS post 와 동일.)
                    eventType: IFRAME_EVENT_MESSAGE.REFRESH_DIMENSIONS,
                    listener: () => {
                        this.getDimensionList();
                        this.postIframeMessage({
                            type: IFRAME_EVENT_MESSAGE.REFRESH_DIMENSIONS,
                        });
                    },
                },
                // Edit Mode leave guard: React re-runs a navigation it refused
                // once the user chose Discard or Publish in its leave modal.
                {
                    eventType: IFRAME_EVENT_MESSAGE.EDIT_MODE_NAV_PROCEED,
                    listener: (event) => {
                        const { pendingRoute } = event?.data || {};
                        if (!pendingRoute) return;
                        // The session is clean now (discarded or published);
                        // React's own dirty mirror may still be on its way.
                        this.isEditModeGuardDirty = false;
                        this.$router.push(pendingRoute);
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.SYNC_EDIT_MODE_GUARD_DIRTY,
                    listener: (event) => {
                        this.isEditModeGuardDirty =
                            event?.data?.shouldWarn ?? false;
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.TRIGGER_TABLE_DOWNLOAD,
                    listener: (event) => {
                        const { file, widgetTitle } = event.data || {};

                        const downloadUrl = URL.createObjectURL(file);
                        const link = document.createElement('a');
                        link.href = downloadUrl;
                        link.download = `${widgetTitle || 'table'}.csv`;
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        URL.revokeObjectURL(downloadUrl);
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.TRIGGER_TABLE_XLSX_DOWNLOAD,
                    listener: (event) => {
                        const { file, widgetTitle } = event.data || {};

                        const downloadUrl = URL.createObjectURL(file);
                        const link = document.createElement('a');
                        link.href = downloadUrl;
                        link.download = `${widgetTitle || 'table'}.xlsx`;
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        URL.revokeObjectURL(downloadUrl);
                    },
                },
                /**
                 * field panel
                 */
                {
                    eventType: IFRAME_EVENT_MESSAGE.TOGGLE_METRIC_PANEL,
                    listener: (e) => {
                        const { data } = e;
                        this.setMetricPanelData(data);
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.TOGGLE_BREAKDOWN_PANEL,
                    listener: (e) => {
                        const { data } = e;
                        this.setBreakdownPanelData(data);
                    },
                },
                {
                    eventType:
                        IFRAME_EVENT_MESSAGE.TOGGLE_CUSTOM_BREAKDOWN_PANEL,
                    listener: (e) => {
                        const { data } = e;
                        this.setCustomBreakdownPanelData(data);
                    },
                },
                {
                    eventType: IFRAME_EVENT_MESSAGE.SET_IFRAME_LAYER,
                    listener: (e) => {
                        this.iframeLayer = e.data?.layer ?? 'none';
                    },
                },
            ],
        };
    },
    created() {
        // What React last reported through SYNC_ROUTE; the Vue route that
        // matches it needs neither a guard nor a sync back (dashboardRouteSync).
        this.reactRoute = null;
        // React posts SYNC_ROUTE on every query change while a view loads;
        // one replace per burst is enough.
        this.applyReactRoute = debounce(this.applyReactRouteNow, 200);
    },
    mounted() {
        this.checkRoleAndRedirect$();

        this.sync$();
        this.currentId = this.$route.params.id;
        this.queryParams = this.$route.query;
        // if (!this.isMobileSize) {
        //     document.querySelector('html').classList.add('overflow-hidden');
        // }

        document.querySelector('html').classList.add('overflow-hidden');

        this.filterHeight = document.querySelector('.top-container');

        this.createClient$();

        this.$nextTick(() => {
            this.observeQuery$();
            this.fetchData$();
            this.observeTeamspaceConfig$();
        });
        window.addEventListener('beforeunload', this.unLoadEvent);
    },
    beforeDestroy() {
        // Vue 2 has no `beforeUnmount` option: removing it there never ran,
        // so a dirty dashboard kept prompting "Leave site?" after it was left.
        window.removeEventListener('beforeunload', this.unLoadEvent);
        this.applyReactRoute.cancel();
        this.removeIframeRef(this.$refs.iframeReactDashboard_);
        this.removeIframeRef(this.$refs.iframeGoalRangeSetting);
        this.removeIframeRef(this.$refs.iframeFilterPanel);
        this.removeIframeRef(this.$refs.iframePanel);
        document.querySelector('html').classList.remove('overflow-hidden');
        this.dashboardClientCreated$.next(false);
        this.setSnackbarSafeBottom(null);
    },
    computed: {
        ...mapGetters('user', [
            'isAdmin',
            'userId',
            'planType',
            'userLang',
            'user',
            'account',
            'accountToken',
        ]),
        ...mapState('user', {
            globalUserAccountId: 'userAccountId',
            dashboardClient: 'dashboardClient',
        }),
        ...mapState('newDashboard', [
            'widgetError',
            'widgetStatusPanelWidgetID',
            'isTakeSnapshotLoading',
            'hubspotBannerHeight',
            'isWidgetFilterOpen',
            'goalRangeSettingData',
            'metricPanelData',
            'breakdownPanelData',
            'customBreakdownPanelData',
        ]),
        ...mapGetters('newDashboard', [
            'currentView',
            'currentWidgetsById',
            'renderWidgetStatusPanel',
            'analyticAccounts',
        ]),
        ...mapState('iframe', ['rectFromReact']),
        ...mapState('dashboardHeader', ['widgetRefresh$']),
        ...mapGetters('view', ['subscriptionWarningVisible', 'showCsmToast']),
        routeMeta: R.pathOr({}, ['$route', 'meta']),
        _accountId() {
            return this.globalUserAccountId || this.cAccountId;
        },
        buildIframeQueryParams() {
            const { queryParams } = this;

            const queries = {
                ...queryParams,
                locale: this.language,
                country: this.user.country,
                token: this.token,
                baseUrl:
                    process.env.VUE_APP_REACT_BASE_URL !==
                    window.location.origin
                        ? window.location.origin
                        : undefined,
            };

            const cleanedQueryParamsEntries = Object.entries(queries).filter(
                ([_, value]) => !R.isNil(value)
            );

            const cleanedQueryParams = Object.fromEntries(
                cleanedQueryParamsEntries
            );
            return new URLSearchParams(cleanedQueryParams).toString();
        },
        iframeUrl() {
            const paramId = this.currentId ?? '';
            const fileExtension =
                process.env.NODE_ENV === `production` ? `.html` : ``;
            // const baseUrl = process.env.VUE_APP_REACT_BASE_URL;

            const queryParams = this.buildIframeQueryParams;

            const baseUrl = REACT_BASE_URL;

            return `${baseUrl}/dashboard${fileExtension}?viewId=${paramId}&${queryParams}`;
        },
        iframeGoalRangeSettingUrl() {
            const paramId = this.currentId ?? '';
            const fileExtension =
                process.env.NODE_ENV === `production` ? `.html` : ``;

            const queryParams = this.buildIframeQueryParams;

            const baseUrl = REACT_BASE_URL;

            return `${baseUrl}/goalRangeSetting${fileExtension}?viewId=${paramId}&${queryParams}`;
        },
        iframePanelUrl() {
            const paramId = this.currentId ?? '';
            const fileExtension =
                process.env.NODE_ENV === `production` ? `.html` : ``;

            const queryParams = this.buildIframeQueryParams;

            const baseUrl = REACT_BASE_URL;

            return `${baseUrl}/panels${fileExtension}?viewId=${paramId}&${queryParams}`;
        },
        iframeFilterPanelUrl() {
            const paramId = this.currentId ?? '';
            const fileExtension =
                process.env.NODE_ENV === `production` ? `.html` : ``;

            const queryParams = this.buildIframeQueryParams;

            const baseUrl = REACT_BASE_URL;

            return `${baseUrl}/filter${fileExtension}?viewId=${paramId}&${queryParams}`;
        },
    },

    render() {
        if (!this.cAccountId) {
            return (
                <PageLoaderWrapper>
                    <RingLoader />
                </PageLoaderWrapper>
            );
        }

        // vue header height: 48px
        //  react header height: 60px
        return (
            <div>
                {/* [IMPROVE] goal range setting dialog => integrate to panel iframe */}
                <Iframe
                    src={this.iframeGoalRangeSettingUrl}
                    resizing={false}
                    customWidth={'100%'}
                    customHeight={'100vh'}
                    ref="iframeGoalRangeSetting"
                    overlayVisible={false}
                    style={{
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        zIndex: isValueExist(this.goalRangeSettingData)
                            ? 12
                            : 0,
                    }}
                />

                {/* panels — MetricPanel / BreakdownPanel 및 관련 overlay 패널 통합 iframe */}
                <Iframe
                    src={this.iframePanelUrl}
                    customWidth={'100%'}
                    customHeight={'100vh'}
                    ref="iframePanel"
                    overlayVisible={false}
                    style={(() => {
                        /**
                         * React → Vue SET_IFRAME_LAYER 메시지로 제어.
                         * none        : 패널 없음 → iframe이 클릭 이벤트를 막지 않도록 z-index 0
                         * secondPanel : MetricPanel/BreakdownPanel 열림 → WidgetSettingPanel 뒤에 위치
                         * customPanel : CustomMetricPanel/CustomBreakdownPanel 열림 → WidgetSettingPanel 위로 올라와야 함
                         *
                         * 모바일 크기일 때는 dashboard iframe 내부의 MobileGuide가 보여야 하므로
                         * panels iframe을 z-index -1로 내림 (z-index는 iframe 경계를 넘지 못함)
                         */
                        if (this.isMobileSize) {
                            return {
                                position: 'fixed',
                                top: 0,
                                right: 0,
                                zIndex: -1,
                            };
                        }

                        const zIndexMap = {
                            none: 0,
                            secondPanel: 10,
                            customPanel: 12,
                        };

                        return {
                            position: 'fixed',
                            top: 0,
                            right: 0,
                            zIndex: zIndexMap[this.iframeLayer] ?? 0,
                        };
                    })()}
                />

                {/* [IMPROVE] filter panel => integrate to panel iframe */}
                <Iframe
                    src={this.iframeFilterPanelUrl}
                    customWidth={'100%'}
                    customHeight={'100vh'}
                    ref="iframeFilterPanel"
                    overlayVisible={false}
                    style={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        zIndex: this.isWidgetFilterOpen ? 11 : 0,
                    }}
                />

                {/* dashaboard */}
                <Iframe
                    src={this.iframeUrl}
                    data-hj-allow-iframe=""
                    ref="iframeReactDashboard_"
                    iframeKey="react-dashboard"
                    customWidth="100vw"
                    // resizing={this.isMobileSize ? true : false}
                    customHeight={
                        this.isMobileSize
                            ? '100vh'
                            : `calc(100vh - ${this.hubspotBannerHeight ?? 0}px)`
                    }
                    resizing={false}
                    noMinHeight={true}
                    overlayVisible={!this.connectionPanel}
                />

                {this.renderCreativeModal()}
                {this.renderWidgetSettingPanel()}
                {this.renderErrorWidgetPanel()}
                {this.renderConnectionPanel()}

                {this.renderDataSource_()}

                <portal-target name="panel_modal" />
                <portal-target name="panel_modal_second" />
            </div>
        );
    },
};

export default WithAccountModal(ReactDashboard);
